import React from "react";
import { IProduct } from "../../vm";
import { Formik } from "formik";
import {
    Grid, FormControl, TextField, Button, Box, Typography,
    Divider, alpha, Stack,
} from "@mui/material";
import { addAProduct, updateAProduct } from "../../Service/ProductService";
import * as yup from "yup";
import { CustomSelect, CustomTextField } from "../../common/Library";
import ImageCropper from "../../common/ImageCropper";
import toast from "react-hot-toast";
import { getFileByBase64, getSignedUrl, uploadFile } from "../../Service/product.service";
import { PRODUCT_UNIT, STATUS_DICT_FOR_PRODUCT } from "../../common/constant";
import { Loading } from "../../common/Loading";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

const productSchema = yup.object().shape({
    title: yup.string().required("required"),
    slug: yup.string().required("required"),
    noOfBurner: yup.string().required("required"),
    materialType: yup.string().required("required"),
    ignitionType: yup.string().required("required"),
});
const productWithOptionSchema = productSchema.shape({
    mrp: yup.number().required("required"),
    sellingPrice: yup.number().required("required"),
});

export interface ProductFormProps {
    onClose: Function;
    data?: IProduct;
}

const SectionLabel = ({ label }: { label: string }) => (
    <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{
            mb: 1.5,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            fontSize: "0.7rem",
        }}
    >
        {label}
    </Typography>
);

const ProductForm: React.FC<ProductFormProps> = ({ onClose, data }) => {
    const [isLoading, setLoading] = React.useState(false as boolean);
    const [product] = React.useState({
        title: "",
        slug: "",
        image: "",
        mrp: 0,
        priceUnit: "piece",
        unitLabel: "",
        sellingPrice: 0,
        status: 0,
        deliveryTime: 0,
        ...data,
    } as IProduct);
    const [selectedImage, setSelectedImage] = React.useState(undefined as any);
    const [manageImageCropDialog, setManageImageCropDialog] = React.useState({
        isOpen: false,
        file: undefined,
        crop: { unit: "%", width: 60, aspect: 4 / 4 },
    } as { isOpen: boolean; file?: any; crop?: any });

    const onDialogClose = (data?: IProduct) => {
        onClose(data);
    };

    const handleFileChange = (files: any) => {
        if (files[0].type.includes("image/")) {
            setSelectedImage(files[0]);
            handleOpenImageCropDialog(files[0]);
        } else {
            toast.error("Select only images");
        }
    };

    const uploadAFile = async (fileName: any, selectedFile: any) => {
        setLoading(true);
        try {
            const result = await getSignedUrl({
                fileName,
                //@ts-ignore
                fileType: "image/png",
                photo_key: "product",
                tab: 2,
                originalname: selectedFile.name,
            });
            if (result.status === "success") {
                await uploadFile(result.data.presignedUrl, selectedFile);
                return result.data.key;
            } else {
                toast.error(`Error while uploading image`);
            }
        } catch (error) {
            //@ts-ignore
            toast.error(`Error while uploading image: ${error.message}`);
        }
        return undefined;
    };

    const handleOpenImageCropDialog = (file: any) => {
        setManageImageCropDialog({ ...manageImageCropDialog, isOpen: true, file });
    };

    const handleCloseImageCropDialog = async (data?: any) => {
        let selectedImg = undefined;
        if (data) {
            selectedImg = await getFileByBase64(data, selectedImage.name);
        }
        setManageImageCropDialog({ ...manageImageCropDialog, file: undefined, isOpen: false });
        setSelectedImage(selectedImg);
    };

    //@ts-ignore
    const isEdit = data?._id != null;

    return (
        <React.Fragment>
            {isLoading && <Loading />}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Formik
                    enableReinitialize
                    initialValues={product}
                    validationSchema={isEdit ? productSchema : productWithOptionSchema}
                    onSubmit={async (values: IProduct, { setSubmitting }) => {
                        let res;
                        let obj = { ...values };
                        setLoading(true);
                        if (obj._id) {
                            if (selectedImage) {
                                let name =
                                    `products/${obj._id}/` +
                                    new Date().getTime() +
                                    "-" +
                                    selectedImage.name.toLowerCase().replace(/ /g, "");
                                //@ts-ignore
                                let fileName = await uploadAFile(name, selectedImage, values._id);
                                if (fileName) {
                                    obj.imageUrl = fileName;
                                }
                            }
                            res = await updateAProduct(obj);
                        } else {
                            res = await addAProduct(obj);
                            if (res.status === "success") {
                                if (selectedImage) {
                                    let data = { ...res.data };
                                    let name =
                                        `products/${res.data._id}/` +
                                        new Date().getTime() +
                                        "-" +
                                        selectedImage.name.toLowerCase().replace(/ /g, "");
                                    let fileName = await uploadAFile(name, selectedImage);
                                    if (fileName) {
                                        data.imageUrl = fileName;
                                        res = await updateAProduct(data);
                                    }
                                }
                            }
                        }
                        if (res.status == "success") {
                            toast.success(
                                `Product ${obj._id ? "updated" : "created"} successfully`
                            );
                            onDialogClose(res.data);
                        } else {
                            toast.error(
                                res?.message ||
                                    `Error while ${values._id ? "updating" : "creating"} product`
                            );
                        }
                        setLoading(false);
                        setSubmitting(false);
                    }}
                >
                    {({ values, errors, handleSubmit }) => (
                        <form onSubmit={handleSubmit}>
                            {/* Basic Info */}
                            <SectionLabel label="Basic Information" />
                            <Grid container spacing={2}>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomTextField name="title" label="Title" />
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomTextField name="slug" label="Slug" />
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomTextField name="brand" label="Brand" type="text" />
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomTextField name="manufacturer" label="Manufacturer" type="text" />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomTextField
                                        multiline={true}
                                        rows={4}
                                        name="description"
                                        label="Description"
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />

                            {/* Pricing & Stock */}
                            <SectionLabel label="Pricing & Stock" />
                            <Grid container spacing={2}>
                                {!isEdit && (
                                    <>
                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                            <CustomSelect
                                                name="priceUnit"
                                                label="Price Unit"
                                                menuList={Object.keys(PRODUCT_UNIT).map((d) => ({
                                                    value: d,
                                                    label: PRODUCT_UNIT[d],
                                                }))}
                                            />
                                        </Grid>
                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                            <CustomTextField name="unitLabel" label="Unit Label" />
                                        </Grid>
                                    </>
                                )}
                                <Grid item lg={4} md={4} sm={6} xs={12}>
                                    <CustomTextField name="mrp" label="MRP" type="number" />
                                </Grid>
                                <Grid item lg={4} md={4} sm={6} xs={12}>
                                    <CustomTextField name="sellingPrice" label="Selling Price" type="number" />
                                </Grid>
                                <Grid item lg={4} md={4} sm={6} xs={12}>
                                    <CustomTextField
                                        name="deliveryTime"
                                        label="Delivery Time (days)"
                                        type="number"
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomSelect
                                        name="status"
                                        label="Status"
                                        menuList={Object.keys(STATUS_DICT_FOR_PRODUCT).map((d) => ({
                                            value: d,
                                            label: STATUS_DICT_FOR_PRODUCT[d],
                                        }))}
                                    />
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomTextField name="warranty" label="Warranty Period" type="text" />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />

                            {/* Specifications */}
                            <SectionLabel label="Specifications" />
                            <Grid container spacing={2}>
                                <Grid item lg={4} md={4} sm={6} xs={12}>
                                    <CustomTextField name="noOfBurner" label="No. of Burners" type="text" />
                                </Grid>
                                <Grid item lg={4} md={4} sm={6} xs={12}>
                                    <CustomTextField name="materialType" label="Material Type" type="text" />
                                </Grid>
                                <Grid item lg={4} md={4} sm={6} xs={12}>
                                    <CustomTextField name="ignitionType" label="Ignition Type" type="text" />
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomTextField name="knobsType" label="Knobs Type" type="text" />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />

                            {/* Image */}
                            <SectionLabel label="Product Image" />
                            {!values.imageUrl && (
                                <Box
                                    sx={{
                                        border: `2px dashed`,
                                        borderColor: alpha("#00897b", 0.4),
                                        borderRadius: 2,
                                        p: 3,
                                        textAlign: "center",
                                        bgcolor: alpha("#00897b", 0.03),
                                        mb: 1,
                                    }}
                                >
                                    <AddPhotoAlternateIcon
                                        sx={{ fontSize: 40, color: alpha("#00897b", 0.4), mb: 1 }}
                                    />
                                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                                        Select a product image
                                    </Typography>
                                    <FormControl>
                                        <TextField
                                            label="Base Image"
                                            type="file"
                                            name="image"
                                            onChange={(event: any) => handleFileChange(event.target.files)}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                inputProps: { accept: "image/x-png,image/gif,image/jpeg" },
                                            }}
                                            size="small"
                                            error={errors.imageUrl && !selectedImage ? true : false}
                                            helperText={errors.imageUrl}
                                        />
                                    </FormControl>
                                    {selectedImage && (
                                        <Typography variant="caption" color="success.main" display="block" mt={1}>
                                            Image selected: {selectedImage.name}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Divider sx={{ my: 2.5 }} />

                            {/* Actions */}
                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                <Button
                                    onClick={() => onClose()}
                                    variant="outlined"
                                    color="inherit"
                                    type="button"
                                    startIcon={<CancelIcon />}
                                    sx={{ textTransform: "none", borderRadius: 2 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    startIcon={<SaveIcon />}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: 2,
                                        bgcolor: "#00897b",
                                        "&:hover": { bgcolor: "#00695c" },
                                    }}
                                >
                                    {values._id ? "Update Product" : "Add Product"}
                                </Button>
                            </Stack>
                        </form>
                    )}
                </Formik>
            </Box>

            {manageImageCropDialog.isOpen && (
                <ImageCropper
                    data={manageImageCropDialog.file}
                    onClose={handleCloseImageCropDialog}
                    crop={manageImageCropDialog.crop}
                />
            )}
        </React.Fragment>
    );
};

export default ProductForm;
